import { useMemo } from 'react';
import { useMutation, UseMutationResult } from '@tanstack/react-query';

import { alarmActivationsAcknowledgeAlarmActivations } from '@crossnokaye/typescript-sdk/funcs/alarmActivationsAcknowledgeAlarmActivations';
import { alarmActivationsClearAlarmActivations } from '@crossnokaye/typescript-sdk/funcs/alarmActivationsClearAlarmActivations';
import {
  AcknowledgeAlarmActivationsRequest,
  AcknowledgeAlarmActivationsResponse,
  ClearAlarmActivationsRequest,
  ClearAlarmActivationsResponse,
} from '@crossnokaye/typescript-sdk/models/operations';
import { useToaster } from '@crossnokaye/ux-common';

import { useAtlasClient } from '@/hooks/use-atlas-client';
import { AtlasQueryWithOrgIdAndAgentId } from '@/queries/types';

import { useGetDeviceControlPointIDMap } from './gaia-api/agent-config';
import { useActiveAlarmActivations } from './gaia-api/alarm-activations';
import { useCurrentFacility } from '../use-current-facility';

/**
 * Acknowledge one or more alarm activations on an agent.
 *
 * @param organizationId The organization ID
 * @param agentId The agent ID
 * @param alarmActivationIds The alarm activation IDs to acknowledge
 */
export const useAcknowledgeAlarmActivations = ({
  orgId,
  agentId,
}: AtlasQueryWithOrgIdAndAgentId): UseMutationResult<
  AcknowledgeAlarmActivationsResponse,
  Error,
  string[]
> => {
  const { addErrorToast, addSuccessToast } = useToaster();
  const client = useAtlasClient();

  return useMutation({
    mutationFn: (alarmActivationIds: string[]) => {
      if (!orgId || !agentId) {
        throw new Error('Organization ID and Agent ID are required');
      }
      if (!alarmActivationIds || alarmActivationIds.length === 0) {
        throw new Error('Alarm activation IDs are required');
      }
      return client<AcknowledgeAlarmActivationsRequest, AcknowledgeAlarmActivationsResponse>({
        atlasFunc: alarmActivationsAcknowledgeAlarmActivations,
        request: {
          acknowledgeAlarmActivationsRequestBody: { alarmActivationIds: alarmActivationIds },
          orgId,
          agentId,
        },
      });
    },
    onSuccess: () => {
      addSuccessToast('Alarm activation(s) acknowledged');
    },
    onError: () => {
      addErrorToast('Failed to acknowledge alarm activation(s)');
    },
  });
};

/**
 * Clear one or more alarm activations on an agent.
 *
 * @param organizationId The organization ID
 * @param agentId The agent ID
 * @param alarmActivationIds The alarm activation IDs to clear
 */
export const useClearAlarmActivations = ({
  orgId,
  agentId,
}: AtlasQueryWithOrgIdAndAgentId): UseMutationResult<
  ClearAlarmActivationsResponse,
  Error,
  string[]
> => {
  const { addErrorToast, addSuccessToast } = useToaster();
  const client = useAtlasClient();

  return useMutation({
    mutationFn: (alarmActivationIds: string[]) => {
      if (!orgId || !agentId) {
        throw new Error('Organization ID and Agent ID are required');
      }
      if (!alarmActivationIds || alarmActivationIds.length === 0) {
        throw new Error('Alarm activation IDs are required');
      }
      return client<ClearAlarmActivationsRequest, ClearAlarmActivationsResponse>({
        atlasFunc: alarmActivationsClearAlarmActivations,
        request: {
          acknowledgeAlarmActivationsRequestBody: { alarmActivationIds: alarmActivationIds },
          orgId,
          agentId,
        },
      });
    },
    onSuccess: () => {
      addSuccessToast('Alarm activation(s) cleared');
    },
    onError: () => {
      addErrorToast('Failed to clear alarm activation(s)');
    },
  });
};

type DeviceAlarmsProps = {
  deviceId: string;
};

/**
 * Returns the active alarm activations for a device grouped by severity.
 * @param deviceId The ID of the device
 * @returns The active alarm activations for the device grouped by severity
 */
export const useDeviceAlarmActivations = ({ deviceId }: DeviceAlarmsProps) => {
  const { agentId } = useCurrentFacility();
  const { data: alarmActivations } = useActiveAlarmActivations({
    agentId,
  });
  const { data: deviceControlPointMap } = useGetDeviceControlPointIDMap();

  // Get the control point IDs for the device from BFF map
  const deviceControlPointIds = deviceControlPointMap?.[deviceId];

  // Filter alarm activations for this device (by device ID or control point IDs)
  return useMemo(() => {
    if (!alarmActivations) return undefined;

    return alarmActivations.filter(
      (alarm) =>
        alarm.device?.id === deviceId ||
        (Array.isArray(alarm.controlPoints) &&
          alarm.controlPoints.some((cp) => cp.id && deviceControlPointIds?.includes(cp.id)))
    );
  }, [alarmActivations, deviceId, deviceControlPointIds]);
};
